import { useId, useState, type ReactNode } from "react";
import type { WhatsAppConversationRow } from "@gioia365/contracts";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTab } from "../api/queries";
import { Button } from "../design/components/Button";
import { Checkbox } from "../design/components/Checkbox";
import { DataTable } from "../design/components/DataTable";
import { Icon } from "../design/components/Icon";
import { Reason } from "../design/components/Reason";
import { Select } from "../design/components/Select";
import { StatusPill } from "../design/components/StatusPill";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { formatNumericDateTime } from "../i18n/format";
import { useT, type Translate } from "../i18n/locale";
import { formatPhoneForDisplay } from "../leads/lead-display";
import { useResultSearch } from "../leads/lead-list-hooks";
import { useLeadView } from "../leads/lead-queries";
import { canActOnLeadOf, conversationScopeOf, useAccess } from "../session/access";
import { inboundKindKey } from "../whatsapp/inbound-kinds";
import { isReplyTarget, ReplyDialog, type ReplyTarget } from "../whatsapp/ReplyDialog";
import { agileStateKey } from "../whatsapp/whatsapp-texts";
import { ConversationsPeriod, ConversationsProblem, ConversationsSearchField, type ConversationsTabProps } from "./ConversationParts";
import { conversationPeopleQuery } from "./conversation-queries";
import { whatsAppListQueryOf } from "./conversation-search";
import { useMarkAllWhatsAppRead, useMarkWhatsAppRead, whatsAppPagesQuery } from "./whatsapp-queries";

// La scheda «WhatsApp» di «Conversazioni» (piano M6, R3, passo 12b; D3, D24; questioni 357, 388, 418–425; mock-up
// `conversazioni.html?vista=whatsapp` e `mockup.js`, `renderWhatsapp`, `waStateText`, `markWhatsappRead`): i WhatsApp nel perimetro di
// chi guarda (`GET /api/conversations/whatsapp`: quelli dei Lead di cui è owner, quelli che ha scritto e quelli arrivati al suo
// numero; con «tutti» tutti; nella «Guarda come…» quelli della persona osservata), dal più recente, a pagine. Periodo, «Di chi»
// (soltanto per chi vede tutti), «Solo da leggere» e la ricerca stanno nell'indirizzo, come le telefonate. Le colonne del mock-up:
// «Quando», «Lead» (o «Numero non in Gioia365» con il numero, questione 357), «Cosa» («Ha scritto il Lead», «Inviato», o «Preparato»
// per un messaggio soltanto preparato), «Messaggio» (il testo fra caporali, «Modello «…»» per un modello, la frase della questione 425
// per ciò che non è un testo), «Con quale WhatsApp», «Stato» (le parole della Timeline: «Da leggere», «Letto da … il …», gli stati di
// Meta, «Dichiarato inviato da …»), «Di chi» per chi vede tutti (la persona del numero per un messaggio del Lead, l'autore per uno in
// uscita, «Gioia365 (automatico)» per la conferma dello «STOP») e l'azione: «Rispondi» (soltanto quando la riga lo offre: un messaggio
// del Lead, da un telefono ancora suo, per chi può rispondere; la conferma automatica dello «STOP» non ce l'ha), oppure «Segna come
// letto» per un messaggio da leggere senza «Rispondi».
// La lettura (questione 423): «Rispondi» su un messaggio da leggere lo segna letto (il mock-up: «Aprire la risposta vuol dire averlo
// letto»); «Segna tutti come letti (N)», con sotto «Quelli da leggere che vedi qui, con questi filtri.», segna quelli da leggere con i
// filtri della scheda arrivati fino all'ora del server della prima pagina che si vede (`until`, il suo `readUntil`: un messaggio
// arrivato dopo, anche con un'ora di Meta di prima, resta da leggere); N sono quelli da leggere fra le righe lette, e quando la
// lista ha altre pagine il numero non si sa e il pulsante non lo dice. Dopo, la lista, il numero del menu e del pulsante della scheda e
// le Timeline si rileggono. Sotto i 720 px di contenuto ogni riga è una scheda, come le telefonate.

type RowProps = {
  row: WhatsAppConversationRow;
  byPerson: boolean;
  timeZone: string;
  canOpenLead: boolean;
  marking: boolean;
  onReply: (row: ReplyTarget) => void;
  onMarkRead: (row: WhatsAppConversationRow) => void;
};

const NOT_CONFIRMED = "not_confirmed";

/** Un messaggio del Lead ancora da leggere. */
const isUnread = (row: WhatsAppConversationRow) => row.message.direction === "in" && row.message.readAt === null;

/** «Stato» per un messaggio che non è «Da leggere»: le parole della Timeline, dal suo stato di adesso. */
function stateText(t: Translate, row: WhatsAppConversationRow, timeZone: string): string {
  const { message } = row;
  if (message.direction === "in") {
    const when = message.readAt === null ? "" : formatNumericDateTime(timeZone, message.readAt);
    return message.readBy === null ? t("timeline.whatsapp.readByPlatform", { when }) : t("timeline.whatsapp.readBy", { name: message.readBy, when });
  }
  switch (message.status) {
    case "prepared":
      return t("timeline.whatsapp.prepared");
    case "declared_sent":
      return row.person ? t("conversations.whatsapp.declaredBy", { name: row.person.displayName }) : t("timeline.whatsapp.declaredSent");
    case "queued":
    case "sent":
    case "delivered":
    case "read": {
      // WhatsApp Agile (R9, Task 27b; WA-7): soltanto «Preso in carico da WhatsApp Agile», come nel mock-up (`WA_LOG`).
      const agile = message.channel === "agile" ? agileStateKey(message.status, message.failureCode) : null;
      if (agile !== null) return t(agile);
      return message.channel === "meta" ? t(`timeline.whatsapp.officialStates.${message.status}`) : t("timeline.whatsapp.other");
    }
    default:
      return t("timeline.whatsapp.other");
  }
}

function StateCell({ row, timeZone }: { row: WhatsAppConversationRow; timeZone: string }) {
  const t = useT();
  if (isUnread(row)) {
    return (
      <StatusPill tone="warning" icon="clock">
        {t("timeline.whatsapp.unread")}
      </StatusPill>
    );
  }
  if (row.message.direction === "out" && row.message.status === "failed") {
    const agile = row.message.channel === "agile" ? agileStateKey("failed", row.message.failureCode) : null;
    let failed: string;
    if (agile !== null) failed = t(agile);
    else failed = t(row.message.failureCode === NOT_CONFIRMED ? "timeline.whatsapp.officialStates.notConfirmed" : "timeline.whatsapp.officialStates.failed");
    return (
      <StatusPill tone="danger" icon="alert">
        {failed}
      </StatusPill>
    );
  }
  return <>{stateText(t, row, timeZone)}</>;
}

/** «Messaggio»: il testo fra caporali, il modello con il suo nome, o la frase di ciò che il Lead ha mandato con la didascalia. */
function MessageCell({ row }: { row: WhatsAppConversationRow }) {
  const t = useT();
  const { message } = row;
  const kind = inboundKindKey(message.contentKind);
  if (kind !== null) {
    return (
      <>
        <span className="g-conversations__message">{t(kind)}</span>
        {message.body !== "" ? <span className="g-table__sub g-conversations__message">{t("conversations.whatsapp.quoted", { text: message.body })}</span> : null}
      </>
    );
  }
  const text = message.direction === "out" && message.template !== null ? t("conversations.whatsapp.template", { name: message.template.name }) : t("conversations.whatsapp.quoted", { text: message.body });
  return <span className="g-conversations__message">{text}</span>;
}

function WhatsAppRowView({ row, byPerson, timeZone, canOpenLead, marking, onReply, onMarkRead }: RowProps) {
  const t = useT();
  // «Rispondi» e «Segna come letto» dicono di quale Lead sono: il nome come descrizione.
  const nameId = useId();
  const { message, lead } = row;
  const inbound = message.direction === "in";
  let what: string;
  if (inbound) what = t("timeline.whatsapp.received");
  else if (message.status === "prepared") what = t("conversations.whatsapp.prepared");
  else what = t("conversations.whatsapp.outbound");
  let person: string;
  if (message.automatic !== null) person = t("timeline.whatsapp.automaticAuthor");
  else person = row.person?.displayName ?? "—";

  let action: ReactNode = null;
  if (isReplyTarget(row)) {
    action = (
      <Button aria-describedby={nameId} onClick={() => onReply(row)}>
        <Icon name="whatsapp" />
        {t("conversations.whatsapp.reply")}
      </Button>
    );
  } else if (isUnread(row)) {
    action = (
      <Button variant="ghost" aria-describedby={nameId} disabled={marking} aria-busy={marking ? "true" : undefined} onClick={() => onMarkRead(row)}>
        <Icon name="check" />
        {t("timeline.whatsapp.markRead")}
      </Button>
    );
  }

  return (
    <tr data-unread={isUnread(row) ? "true" : undefined}>
      <td data-label={t("conversations.whatsapp.columns.when")}>{formatNumericDateTime(timeZone, message.at)}</td>
      <th scope="row">
        {lead === null ? (
          <>
            <span id={nameId} className="g-conversations__lead">
              {t("conversations.whatsapp.unknownNumber")}
            </span>
            {row.leadE164 !== null ? <span className="g-table__sub">{formatPhoneForDisplay(row.leadE164)}</span> : null}
          </>
        ) : (
          <>
            {canOpenLead ? (
              <Link to="/lead/$leadId" params={{ leadId: lead.id }} id={nameId}>
                {lead.displayName ?? "—"}
              </Link>
            ) : (
              <span id={nameId} className="g-conversations__lead">
                {lead.displayName ?? "—"}
              </span>
            )}
            {lead.salonName ? <span className="g-table__sub">{lead.salonName}</span> : null}
          </>
        )}
      </th>
      <td data-label={t("conversations.whatsapp.columns.what")}>
        <span className="g-pill" data-tone={inbound ? "accent" : "neutral"}>
          <Icon name={inbound ? "down" : "up"} />
          {what}
        </span>
      </td>
      <td data-label={t("conversations.whatsapp.columns.message")}>
        <MessageCell row={row} />
      </td>
      <td data-label={t("conversations.whatsapp.columns.channel")}>{t(`conversations.whatsapp.channels.${message.channel}`)}</td>
      <td data-label={t("conversations.whatsapp.columns.state")}>
        <StateCell row={row} timeZone={timeZone} />
      </td>
      {byPerson ? <td data-label={t("conversations.whatsapp.columns.person")}>{person}</td> : null}
      <td className="g-conversations__action">{action}</td>
    </tr>
  );
}

export function WhatsAppTab({ search, update, writeQ }: ConversationsTabProps) {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const view = useLeadView();
  const toast = useToast();
  const baseId = useId();
  // «Di chi» e la colonna «Di chi» soltanto per chi vede le conversazioni di tutti, con la regola del server.
  const byPerson = conversationScopeOf(access) === "all";
  const query = whatsAppListQueryOf(search, byPerson);
  const pages = useInfiniteQuery({ ...whatsAppPagesQuery(tab, query, view), placeholderData: keepPreviousData });
  const people = useQuery(conversationPeopleQuery(tab));
  const markRead = useMarkWhatsAppRead(tab, view);
  const markAll = useMarkAllWhatsAppRead(tab, view);
  const timeZone = access.context.settings.timeZone;
  const shown = pages.data && !pages.isPlaceholderData ? pages.data.pages.flatMap((page) => page.rows) : null;
  const [replying, setReplying] = useState<ReplyTarget | null>(null);
  const { text, setText } = useResultSearch(search.q, writeQ);

  const peopleOptions = (people.data ?? []).map((person) => ({ value: person.membershipId, label: person.displayName }));
  // Una persona dell'indirizzo che non è fra le scelte (un link di prima) resta scelta, con il nome che le righe le danno.
  const whoOptions = [{ value: "all", label: t("conversations.wholeTeam") }, ...peopleOptions];
  if (search.chi !== undefined && !whoOptions.some((option) => option.value === search.chi)) {
    const named = shown?.find((row) => row.person?.membershipId === search.chi)?.person?.displayName;
    whoOptions.push({ value: search.chi, label: named ?? search.chi });
  }

  function reply(row: ReplyTarget) {
    // Aprire la risposta vuol dire averlo letto (il mock-up).
    if (isUnread(row)) markRead.mutate({ messageId: row.message.id, leadId: row.lead.id });
    setReplying(row);
  }

  function readOne(row: WhatsAppConversationRow) {
    markRead.mutate(
      { messageId: row.message.id, leadId: row.lead?.id ?? null },
      { onError: (error) => toast.show(messageForError(t, error), "warning") },
    );
  }

  function readAll(until: string | null) {
    markAll.mutate(
      { query, until },
      {
        onSuccess: () => toast.show(t("conversations.whatsapp.markedAll"), "success"),
        onError: (error) => toast.show(messageForError(t, error), "warning"),
      },
    );
  }

  let results: ReactNode;
  if (shown === null) {
    results = pages.isError ? (
      <ConversationsProblem error={pages.error} onRetry={() => void pages.refetch()} />
    ) : (
      <p className="g-conversations__loading" role="status">
        {t("common.loading")}
      </p>
    );
  } else if (shown.length === 0) {
    results = (
      <div className="g-conversations__empty">
        <p>
          <strong>{t("conversations.whatsapp.empty")}</strong>
        </p>
        <p>{t("conversations.whatsapp.emptyHint")}</p>
      </div>
    );
  } else {
    const unread = shown.filter(isUnread);
    const hintId = `${baseId}-read-all`;
    results = (
      <>
        {pages.isError ? <ConversationsProblem error={pages.error} onRetry={() => void pages.refetch()} /> : null}
        {unread.length > 0 ? (
          <div className="g-conversations__read-all">
            <Button aria-describedby={hintId} disabled={markAll.isPending} aria-busy={markAll.isPending ? "true" : undefined} onClick={() => readAll(pages.data?.pages[0]?.readUntil ?? null)}>
              <Icon name="check" />
              {pages.hasNextPage ? t("conversations.whatsapp.markAllUnknown") : t("conversations.whatsapp.markAll", { count: unread.length })}
            </Button>
            <span id={hintId} className="g-conversations__hint">
              {t("conversations.whatsapp.markAllHint")}
            </span>
          </div>
        ) : null}
        <Reason>{t("conversations.whatsapp.note")}</Reason>
        <div className="g-conversations__table g-conversations__whatsapp">
          <DataTable caption={t("conversations.whatsapp.caption")}>
            <thead>
              <tr>
                <th scope="col">{t("conversations.whatsapp.columns.when")}</th>
                <th scope="col">{t("conversations.whatsapp.columns.lead")}</th>
                <th scope="col">{t("conversations.whatsapp.columns.what")}</th>
                <th scope="col">{t("conversations.whatsapp.columns.message")}</th>
                <th scope="col">{t("conversations.whatsapp.columns.channel")}</th>
                <th scope="col">{t("conversations.whatsapp.columns.state")}</th>
                {byPerson ? <th scope="col">{t("conversations.whatsapp.columns.person")}</th> : null}
                <th scope="col">
                  <span className="g-visually-hidden">{t("conversations.whatsapp.columns.action")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <WhatsAppRowView
                  key={row.message.id}
                  row={row}
                  byPerson={byPerson}
                  timeZone={timeZone}
                  // Il Lead si apre con le regole del server (`canActOnLead`), come nelle telefonate.
                  canOpenLead={row.lead !== null && canActOnLeadOf(access, "view", row.lead.currentOwner?.membershipId ?? null)}
                  marking={markRead.isPending && markRead.variables?.messageId === row.message.id}
                  onReply={reply}
                  onMarkRead={readOne}
                />
              ))}
            </tbody>
          </DataTable>
        </div>
        {pages.hasNextPage ? (
          <div>
            <Button onClick={() => void pages.fetchNextPage()} disabled={pages.isFetchingNextPage}>
              {t("conversations.whatsapp.more")}
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      <div className="g-conversations__toolbar">
        <ConversationsPeriod search={search} update={update} />
        <div className="g-conversations__filters">
          {byPerson ? (
            <Select id={`${baseId}-who`} label={t("conversations.who")} value={search.chi ?? "all"} options={whoOptions} onChange={(value) => update({ chi: value === "all" ? undefined : value })} />
          ) : null}
          <Checkbox
            id={`${baseId}-unread`}
            className="g-conversations__audio"
            label={t("conversations.whatsapp.unreadOnly")}
            checked={search.daleggere === 1}
            onChange={(checked) => update({ daleggere: checked ? 1 : undefined })}
          />
        </div>
        <ConversationsSearchField id={`${baseId}-search`} text={text} onChange={setText} />
      </div>
      <div className="g-conversations__results" aria-busy={shown === null && !pages.isError ? true : undefined}>
        {results}
      </div>
      {replying ? <ReplyDialog key={replying.message.id} tab={tab} row={replying} timeZone={timeZone} onClose={() => setReplying(null)} /> : null}
    </>
  );
}
